import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export type KitTranslateFn = (key: string, values?: Record<string, unknown>) => string;

/**
 * Translate function for kit UI that accepts i18next interpolation values
 * (e.g. {{count}}, {{limit}}). The project's shared useTranslateFn types the
 * options as UseTranslationOptions only, which rejects interpolation values, so
 * the kit components use this thin wrapper instead.
 *
 * Memoized on the i18next `t` (stable per render): a fresh wrapper each render
 * would churn the identity of any useCallback/useEffect that depends on it
 * (e.g. CompanionsCatalog's reload effect would loop forever).
 */
export const useKitTranslate = (): KitTranslateFn => {
  const { t } = useTranslation();
  return useCallback((key, values) => {
    // The installed i18next types are stricter than this project's tsc supports,
    // so cast t to a loose signature (same approach as utils/useTranslateFn).
    const translate = t as unknown as (k: string, v?: Record<string, unknown>) => string;
    return values ? translate(key, values) : translate(key);
  }, [t]);
};

export default useKitTranslate;
